/**
 * PATH: packages/email-hub/src/feature/hooks/useJunkActions.ts
 * FILE_ID: EMAILHUB-USEJUNKACTIONS-001
 * PURPOSE: Shared junk verbs for an email — bin THIS one now, or arm an
 *          auto-junk rule (retro-sweep existing + prospective auto-bin of
 *          future mail) by exact sender or whole domain. Wraps the
 *          backend mutations + toasts so every junk surface behaves
 *          identically.
 * WHY:     Both the reader-header `JunkButton` and the inbox-card
 *          `CardJunkControl` need the exact same junk behaviour. Keeping
 *          the verbs in one hook stops the two surfaces from drifting
 *          (operator: junk must "behave identically everywhere").
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────────────┐   ┌────────────────┐
 *   │ email + cb   │ → │ bulkAction / armRule    │ → │ toast + refetch│
 *   └──────────────┘   └─────────────────────────┘   └────────────────┘
 * DEPENDENCIES:
 *   - ./useEmails (useEmailActions): bulkAction + createAutoJunkRule
 *   - ../lib/junkTargets (deriveJunkTargets): pure address → match values
 *   - ../../_internal/components/Toast: feedback
 * RELATED:
 *   - ../components/JunkButton.tsx, ../components/CardJunkControl.tsx
 */

import { useCallback, useRef, useState } from 'react'

import { useToast } from '../../_internal/components/Toast'
import { useEmailActions, type Email } from './useEmails'
import { deriveJunkTargets } from '../lib/junkTargets'

export type JunkMatchType = 'sender_domain' | 'sender_exact'

export interface UseJunkActionsResult {
  /** A junk/rule mutation is in flight. */
  busy: boolean
  /** Bin THIS email now (soft-bin, recoverable from Trash). */
  junkNow: () => Promise<void>
  /** Arm an auto-junk rule (retro-sweep + prospective) for sender/domain. */
  armRule: (matchType: JunkMatchType) => Promise<void>
  /** Exact sender address ('' when the from-address is missing/malformed). */
  exact: string
  /** Sender domain ('' when unavailable). */
  domain: string
  /** Whether any rule option can be offered (a valid sender exists). */
  hasOptions: boolean
}

export function useJunkActions(
  email: Pick<Email, 'id' | 'from_address'>,
  onRefetch?: () => void,
  /**
   * Operator (2026-06-17): "I clicked Junk yet it did not vanish and go to
   * the next email." Junk is TERMINAL — it bins the email — so when the
   * host provides `onResolved` it owns dropping the row + advancing the
   * reader to the next message (mirrors `handleBinRow`). Omitted → fall
   * back to a plain `onRefetch` (e.g. the inbox-card chip, tests).
   */
  onResolved?: (emailId: string) => void,
): UseJunkActionsResult {
  const { bulkAction, createAutoJunkRule } = useEmailActions()
  const { toast } = useToast()
  const [busy, setBusy] = useState(false)

  const { exact, domain } = deriveJunkTargets(email.from_address)

  // Terminal: advance the reader if the host wired it, else refetch.
  const resolve = useCallback(() => {
    if (onResolved) onResolved(email.id)
    else onRefetch?.()
  }, [onResolved, onRefetch, email.id])

  // Rule arming gates on its OWN in-flight lock, not `busy`: one caller is
  // the post-junk toast action, which can fire before/while other junk state
  // settles. A ref (not state) so a double-click can't race a re-render.
  const armingRef = useRef(false)

  const armRule = useCallback(
    async (matchType: JunkMatchType) => {
      if (armingRef.current) return
      armingRef.current = true
      setBusy(true)
      try {
        const res = await createAutoJunkRule(email.id, matchType)
        const n = res?.binned_count ?? 0
        toast(
          `Junk rule armed for ${res?.rule?.match_value ?? 'this sender'} - binned ${n} email${
            n === 1 ? '' : 's'
          }. Undo in Tags & Rules.`,
          'success',
        )
        resolve()
      } catch {
        toast('Could not arm the junk rule', 'error')
      } finally {
        armingRef.current = false
        setBusy(false)
      }
    },
    [createAutoJunkRule, email.id, toast, resolve],
  )

  const junkNow = useCallback(async () => {
    if (busy) return
    setBusy(true)
    try {
      // 'junk' is the spam-intent soft-bin: same status='binned' +
      // is_archived as 'delete' (so the row leaves the inbox + the kanban
      // card is tombstoned) BUT it also stamps category='junk' and writes
      // a reversible EmailAutoJunkEvent(MANUAL) so the manual junk is
      // audited + recoverable from the junk log. A generic Bin uses
      // 'delete' (no spam signal). 'trash' is NOT a bulk action (the
      // ChoiceField rejects it).
      await bulkAction([email.id], 'junk')
      // The RULE OFFER FOLLOWS the cheap action instead of gating it
      // (operator 2026-07-07: "I can't just junk this email... it has in
      // the drop down two options which are rule creating instead").
      // Bare junk always junks THIS email; the escalation rides the
      // success toast as a one-tap follow-up when a sender is known.
      toast('Junked - moved to bin', 'success', {
        action: exact
          ? {
              label: `Always junk ${exact}`,
              onClick: () => void armRule('sender_exact'),
            }
          : undefined,
      })
      resolve()
    } catch {
      toast('Could not junk this email', 'error')
    } finally {
      setBusy(false)
    }
  }, [busy, bulkAction, email.id, exact, toast, resolve, armRule])

  return { busy, junkNow, armRule, exact, domain, hasOptions: Boolean(exact || domain) }
}

export default useJunkActions
